import { useState } from 'react'
import { useExecutarTransicao } from '../../../api/queries/gmuds'
import type { AcaoDisponivel } from '../../../types/gmud'
import { ehDestrutiva } from './acoesDestrutivas'

/** O que quem desenha os botões precisa saber e chamar. */
export interface AcionamentoDeTransicao {
  /** Dispara a ação: abre o diálogo que ela exigir, ou executa direto quando não exige nenhum. */
  acionar: (acao: AcaoDisponivel) => void
  /** A ação parada esperando justificativa, quando há uma. */
  aguardandoMotivo: AcaoDisponivel | null
  /** A ação parada esperando confirmação, quando há uma. */
  aguardandoConfirmacao: AcaoDisponivel | null
  confirmarComMotivo: (motivo: string) => void
  confirmar: () => void
  cancelar: () => void
  executando: boolean
  falhou: boolean
  erro: unknown
  /** A última ação disparada — é o que a região de anúncios do quadro precisa nomear. */
  ultimaAcao: AcaoDisponivel | null
  /** O servidor confirmou a última transição. */
  concluida: boolean
}

/**
 * Executar uma transição, com a pausa que cada uma exige — num lugar só.
 *
 * **Este arquivo é o que impede o caminho paralelo.** A tela de detalhe e o quadro chamam a mesma
 * função, então uma regra nova entra nas duas de uma vez. Foi a existência de três cópias da mesma
 * decisão — a máquina de estados, o controlador e a tela — que, no sistema anterior, fez o menu
 * oferecer "Aprovar" para quem o backend recusava, e ninguém percebeu por meses.
 *
 * **Não há aqui nenhum `if` sobre status, papel ou destino.** Quem decide o que pode ser feito é o
 * servidor, em `/transicoes-possiveis`; o que este código decide é só a PAUSA antes de executar:
 * justificativa quando o servidor a exige (`exigeMotivo`, lido de `ref_transicao_permitida`),
 * confirmação quando a ação desfaz trabalho já feito (`acoesDestrutivas.ts`, que é aparência), e
 * nada quando não é o caso. Escrever a justificativa já é pausa suficiente: por isso quem exige
 * motivo não passa antes por um segundo diálogo.
 *
 * @param gmudId identificador interno da solicitação
 * @returns o acionamento: o que chamar, o que está esperando e como foi
 */
export function useAcionarTransicao(gmudId: number): AcionamentoDeTransicao {
  const executar = useExecutarTransicao(gmudId)
  const [aguardandoMotivo, setAguardandoMotivo] = useState<AcaoDisponivel | null>(null)
  const [aguardandoConfirmacao, setAguardandoConfirmacao] = useState<AcaoDisponivel | null>(null)
  const [ultimaAcao, setUltimaAcao] = useState<AcaoDisponivel | null>(null)

  function acionar(acao: AcaoDisponivel) {
    executar.reset()
    setUltimaAcao(acao)
    if (acao.exigeMotivo) {
      setAguardandoMotivo(acao)
      return
    }
    if (ehDestrutiva(acao.comando)) {
      setAguardandoConfirmacao(acao)
      return
    }
    executar.mutate({ comando: acao.comando })
  }

  function confirmarComMotivo(motivo: string) {
    if (!aguardandoMotivo) {
      return
    }
    executar.mutate(
      { comando: aguardandoMotivo.comando, motivo },
      { onSuccess: () => setAguardandoMotivo(null) },
    )
  }

  function confirmar() {
    if (!aguardandoConfirmacao) {
      return
    }
    executar.mutate(
      { comando: aguardandoConfirmacao.comando },
      { onSuccess: () => setAguardandoConfirmacao(null) },
    )
  }

  /** Fecha o diálogo aberto sem executar nada — com 12 das 18 transições pedindo justificativa,
   *  desistir no diálogo é o caso comum, e não a exceção. */
  function cancelar() {
    setAguardandoMotivo(null)
    setAguardandoConfirmacao(null)
  }

  return {
    acionar,
    aguardandoMotivo,
    aguardandoConfirmacao,
    confirmarComMotivo,
    confirmar,
    cancelar,
    executando: executar.isPending,
    falhou: executar.isError,
    erro: executar.error,
    ultimaAcao,
    concluida: Boolean(executar.isSuccess),
  }
}
